import React, { useEffect, useState } from "react";
import Div100vh from "react-div-100vh";
import cGif from "@/assets/images/pp.gif";
import { FUN_FACT_MESSAGES } from "@/lib/data";
import { PulseLoader } from "react-spinners";

/**
 * How long to wait before offering the tenant a way out.
 *
 * Every screen that opts in (see `recoverable`) is waiting on a plain GET that normally answers in
 * well under two seconds. Twenty seconds is roughly ten times that, so a slow-but-working load on a
 * poor connection finishes long before this fires — and it lands just after the only duration this
 * screen ever advertises ("20s to go"), so the two never contradict each other.
 */
const RECOVERY_PROMPT_AFTER_SECONDS = 20;

/**
 * @param {object} props
 * @param {boolean} props.recoverable
 *   Opt in to the "Taking longer than usual → Try again" escape hatch. **Defaults to false, and
 *   that default is deliberate.**
 *
 *   This screen renders in nine places, and in two of them a reload is actively harmful:
 *
 *   - `components/banking/bank_details` shows it while `POST banking/start` is in flight, right
 *     before handing off to the vendor. Reloading there can open a SECOND banking session, which
 *     costs the tenant one of their few allowed attempts — the exact harm the attempt-limit work
 *     exists to prevent.
 *   - `ProcessDisbursal` shows it during a genuine socket-driven wait that can legitimately run for
 *     minutes. A prompt there would fire during normal, healthy operation.
 *
 *   So the escape hatch is opted into per call site, only where the wait is a pure GET and a reload
 *   simply re-reads the server. Anything not explicitly opted in keeps today's behaviour exactly.
 *
 *   Note the prompt is **non-destructive**: it cancels nothing, fires no request, and navigates
 *   nowhere. It only appears. If the data lands at second 21 the screen unmounts and the offer goes
 *   with it — so on a slow-but-working connection this changes nothing the tenant acts on.
 */
export default function ProcessingScreen({ theme, timer = false, recoverable = false }) {
  const [timeLeft, setTimeLeft] = useState(20);
  const [messageIndex, setMessageIndex] = useState(() =>
    Math.floor(Math.random() * FUN_FACT_MESSAGES.length)
  );
  const [showRecovery, setShowRecovery] = useState(false);

  useEffect(() => {
    const timerId = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          return 15;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(timerId);
  }, []);

  /**
   * The screen has no way to know its caller's fetch failed — the shared helpers swallow errors and
   * resolve as if nothing happened, so a dead load and a slow one look identical from here. Rather
   * than guess, wait out a period no healthy GET could occupy and then simply offer a retry.
   *
   * Without this the tenant sits on an animation with a countdown that resets forever, being told
   * we are working while nothing is running. Measured: twenty seconds after the connection came
   * back, the screen had not recovered on its own; a manual reload put the tenant on the correct
   * next step immediately. They just had no reason to think of reloading.
   */
  useEffect(() => {
    if (!recoverable) return undefined;
    const promptId = setTimeout(
      () => setShowRecovery(true),
      RECOVERY_PROMPT_AFTER_SECONDS * 1000
    );
    return () => clearTimeout(promptId);
  }, [recoverable]);

  useEffect(() => {
    const messageTimer = setInterval(() => {
      setMessageIndex(
        (prevIndex) => (prevIndex + 1) % FUN_FACT_MESSAGES.length
      );
    }, 5000);

    return () => clearInterval(messageTimer);
  }, []);

  return (
    <Div100vh className="relative flex flex-col justify-between object-cover bg-[#DCD8D2] bg-no-repeat bg-cover">
      <div className="absolute z-10 bottom-0">
        <div className="absolute bg-gradient-to-b from-[#DCD8D2] to-[#DCD8D2]/5 w-full h-[30%]" />
        <img src={cGif} alt="Animated Gif" className="aspect-square" />
      </div>
      <div
        className={`flex flex-col ${
          timer ? "justify-between" : "justify-center mt-10"
        } items-center`}
      >
        {timer && (
          <div className="text-lg font-medium mt-8 text-[#999] font-sans">
            {timeLeft > 0 ? (
              <div className="font-sans">
                <span className="text-black">{timeLeft}s</span> to go!
              </div>
            ) : (
              "almost there..."
            )}
          </div>
        )}
        <div className="z-10 flex flex-col items-start justify-center gap-8 h-2/3 w-full text-[#222] p-12">
          <span
            className={`flex items-center gap-2 text-2xl ${theme?.headingColor} font-semibold`}
          >
            Processing{" "}
            <PulseLoader color="black" className="size-4 aspect-square" />
          </span>
          <span
            key={messageIndex}
            className="text-[#888] italic opacity-100 transition-opacity duration-500 ease-in-out"
          >
            {FUN_FACT_MESSAGES[messageIndex]}
          </span>

          {/*
            Plain words and one obvious action. "Taking longer than usual" is honest without
            claiming to know what went wrong, and a full reload is what was actually verified to
            recover the tenant — the app re-reads the server, which is the source of truth, and
            lands them on their correct next step.
          */}
          {showRecovery && (
            <div className="flex flex-col items-start gap-3">
              <span className="text-[#222] text-sm">
                This is taking longer than usual.
              </span>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="rounded-full bg-[#222] px-6 py-3 text-sm font-semibold text-white"
              >
                Try again
              </button>
            </div>
          )}
        </div>
      </div>
    </Div100vh>
  );
}
